import { useEffect, useState } from "react";
import { Trophy, X, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

/**
 * Rank-up celebration banner shown on the dashboard the first time a member
 * crosses a new rank. Hits GET /dist/rank-status, and on dismiss stamps the
 * new rank via POST /dist/rank-ack so it never reappears for that rank.
 */
export default function RankCongratsBanner() {
  const [info, setInfo] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/dist/rank-status")
      .then((r) => { if (alive) { setInfo(r.data); setVisible(!!r.data?.should_show_congrats); } })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!visible || !info?.current) return null;

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try { await api.post("/dist/rank-ack"); } catch { /* non-blocking */ }
    setVisible(false);
  };

  const rank = info.current;

  return (
    <div
      data-testid="rank-congrats-banner"
      className="relative mb-6 overflow-hidden rounded-sm border border-gold/50 bg-gradient-to-r from-royal via-royal-deep to-navy text-white shadow-lg animate-in fade-in slide-in-from-top-2 duration-500"
    >
      <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-gold/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-rose/20 blur-3xl pointer-events-none" />
      <button
        type="button"
        onClick={dismiss}
        data-testid="rank-congrats-dismiss"
        className="absolute top-3 right-3 text-white/70 hover:text-white p-1.5 rounded-sm hover:bg-white/10"
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
      <div className="relative px-6 md:px-8 py-6 md:py-7 flex flex-col md:flex-row items-start md:items-center gap-5">
        <div className="w-16 h-16 md:w-20 md:h-20 shrink-0 grid place-items-center bg-gold/20 border border-gold/40 rounded-sm">
          <Trophy size={34} className="text-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.3em] text-gold/90 inline-flex items-center gap-1.5">
            <Sparkles size={12} /> Congratulations
          </div>
          <h2 className="font-serif text-2xl md:text-3xl leading-tight mt-1.5">
            You&apos;ve reached <span className="text-gold">{rank.label}</span>!
          </h2>
          <p className="text-sm text-white/80 mt-2 max-w-2xl leading-relaxed">
            {info.annual_pairs} matched pair{info.annual_pairs === 1 ? "" : "s"} qualified this rank.
            {rank.reward > 0 && <> Your <b className="text-gold">₹{Number(rank.reward).toLocaleString("en-IN")}</b> rank reward has been credited to your Reward Wallet.</>}
            {rank.trip && <> Trip Fund: <b className="text-gold">{rank.trip}</b>.</>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link
            to="/rewards"
            data-testid="rank-congrats-rewards"
            className="inline-flex items-center gap-1.5 bg-gold text-navy hover:bg-gold-deep px-4 py-2 text-xs uppercase tracking-widest rounded-sm font-medium"
          >
            <Trophy size={13} /> View Rewards
          </Link>
          <button
            type="button"
            onClick={dismiss}
            data-testid="rank-congrats-continue"
            className="border border-white/30 text-white hover:bg-white/10 px-4 py-2 text-xs uppercase tracking-widest rounded-sm"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
